import { useMutation } from '@tanstack/react-query';
import { get, includes, isEmpty, isUndefined, keyBy } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { ILocation, ILocationGroup } from 'src/interfaces';
import { queryKeys, requestCCApi } from 'src/utils';

import { fetchLocationGroups } from '../locationGroup';
import { narrativeGenerators } from '../userActions/helper';
import { fetchViewLocationsByGroupsWithoutPagination } from './useViewLocationsByGroups';

type locationHqName = {
  string: {
    locationCode: string;
    hqName: string;
  };
};

export const useUpdateLocations = (onSuccessCallback?: () => void) => {
  return useMutation({
    mutationFn: updateLocations,
    onSuccess: () => {
      queryClient.refetchQueries(queryKeys.locationTags.invalidateAll());
      queryClient.invalidateQueries(queryKeys.locations.invalidateAll());
      queryClient.invalidateQueries(queryKeys.viewLocationsByGroups.all());
      queryClient.invalidateQueries(queryKeys.viewLocations.invalidateAll());
      queryClient.invalidateQueries(queryKeys.scheduleDayparts.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.scheduleDaypartOverrides.invalidateAll(),
      );
      queryClient.invalidateQueries(queryKeys.locationListing.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.getLocationGroupsWithLocationCount.invalidateAll(),
      );
      onSuccessCallback?.();
    },
  });
};

const getLocationGroupNames = (
  locationGroups: Array<ILocationGroup>,
  newGroupIds: Array<number>,
  currentGroupIds: Array<number>,
): Array<string> => {
  if (isEmpty(currentGroupIds)) {
    const locationGroupNames = locationGroups
      .filter((locationGroup) => includes(newGroupIds, locationGroup.id))
      .map((locationGroup) => locationGroup.name);

    return locationGroupNames;
  } else {
    const newIds = newGroupIds.filter((id) => !includes(currentGroupIds, id));
    const locationGroupNames = locationGroups
      .filter((locationGroup) => includes(newIds, locationGroup.id))
      .map((locationGroup) => locationGroup.name);

    return locationGroupNames;
  }
};

const getLocationHqName = async (
  locationCodes: Array<string>,
  hasHqInfo: boolean,
): Promise<locationHqName> => {
  const needsHqLookup = !hasHqInfo && !isEmpty(locationCodes);

  if (needsHqLookup) {
    const filter = [`locations.location_code.list=${locationCodes.join('|')}`];
    const viewLocations = await queryClient.fetchQuery({
      queryKey: queryKeys.viewLocations.all(['hqName', ...locationCodes]),
      queryFn: () => fetchViewLocationsByGroupsWithoutPagination(filter),
      staleTime: Infinity,
    });

    const newLocation = viewLocations.map((viewLocation) => ({
      locationCode: viewLocation.locations?.location_code ?? '',
      hqName: viewLocation.hq?.name ?? '',
    }));

    return keyBy(newLocation, 'locationCode') as any;
  }

  return {} as locationHqName;
};

const TRANSACTION_BATCH_SIZE = 50;

const updateLocations = async (payload: ILocation[]) => {
  const locationGroups: Array<ILocationGroup> = await queryClient.fetchQuery({
    queryKey: queryKeys.locationGroups.all(),
    queryFn: () => fetchLocationGroups([]),
    staleTime: Infinity,
  });
  const locationHqNames = await getLocationHqName(
    payload.map((location) => location.location_code),
    payload.every((location) => !isEmpty(location.hq)),
  );

  const buildData = (locations: ILocation[]) => ({
    data: locations.map((location) => ({
      resource: COLLECTIONS.LOCATIONS,
      method: 'PATCH',
      id: location.location_code,
      data: {
        ...(!isUndefined(location.config) && { config: location.config }),
        ...(location.location_group_id && {
          location_group_id: location.location_group_id,
        }),
        ...(location.location_tag_id && {
          location_tag_id: location.location_tag_id,
        }),
        narrative: getLocationGroupNames(
          locationGroups ?? [],
          location.location_group_id,
          location.locationGroups?.map((locationGroup) => locationGroup.id) ??
            [],
        )
          ?.map((locationGroupName) =>
            narrativeGenerators[COLLECTIONS.LOCATIONS][USER_ACTIONS.CREATE](
              location.hq?.name ??
                get(locationHqNames, location.location_code, { hqName: '' })
                  .hqName,
              location.location_code,
              locationGroupName,
            ),
          )
          .join(', '),
      },
    })),
  });

  const batches: ILocation[][] = [];

  for (let i = 0; i < payload.length; i += TRANSACTION_BATCH_SIZE) {
    batches.push(payload.slice(i, i + TRANSACTION_BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map((batch) =>
      requestCCApi({
        url: '/transaction',
        method: 'POST',
        data: buildData(batch),
      }),
    ),
  );

  return results[results.length - 1];
};
